// Sprint 2 PR-D2 — Central Brain Actions view (read-only).
// Endpoint: GET /api/v2/actions
// No approve/reject/execute button (Sprint 3 council vote akışı sonrası).

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

export interface PolicyGateView {
  actionLevel?: number;
  humanRequired?: boolean;
  councilRequired?: false | 'low' | 'medium' | 'high' | 'critical';
  executeAllowed?: boolean;
  outboundAllowed?: boolean;
  llmAllowed?: boolean;
  reasons?: string[];
  source?: string;
  decidedAt?: string;
}

export interface ActionRow {
  id: string;
  tenantId: string;
  eventId: string | null;
  agentRunId: string | null;
  actionType: string;
  title: string;
  description: string | null;
  proposedPayload: unknown;
  riskLevel: string;
  approvalRequired: boolean;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Sprint 3 PR-E4 — policy decision snapshot (legacy satırlar `{}`).
  policyGate?: PolicyGateView | Record<string, never>;
}

export interface ActionsSummary {
  total: number;
  byStatus: Record<string, number>;
  byActionType: Record<string, number>;
  byRiskLevel: Record<string, number>;
  byApprovalRequired: { true: number; false: number };
}

interface ActionsResponse { ok: true; actions: ActionRow[]; summary: ActionsSummary; }

export interface ActionsFilters {
  status?: string;
  riskLevel?: string;
  actionType?: string;
  approvalRequired?: 'true' | 'false';
  eventId?: string;
  agentRunId?: string;
  tenantId?: string;
  limit?: number;
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; actions: ActionRow[]; summary: ActionsSummary; filters: ActionsFilters }
  | { kind: 'error'; message: string; filters: ActionsFilters };

let state: ViewState = { kind: 'idle' };
let filters: ActionsFilters = { limit: 100 };

export async function loadCbActions(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<ActionsResponse>('/api/v2/actions', filters as Record<string, string | number | undefined>);
  if (r.ok) state = { kind: 'ready', actions: r.data.actions, summary: r.data.summary, filters: { ...filters } };
  else state = { kind: 'error', message: r.message ?? r.error, filters: { ...filters } };
}

export function renderCbActions(): void {
  const root = document.getElementById('view-cb-actions');
  if (!root) return;
  root.innerHTML = renderCbActionsHtml(state);
  attachHandlers();
}

export function renderCbActionsHtml(s: ViewState): string {
  const f = (s.kind === 'ready' || s.kind === 'error') ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Actions</div>
        <div class="view-sub">central_brain.actions — claim/lease'li makine iş kuyruğu; Pano'daki Kanban'dan (Paperclip issues) bağımsız · read-only (approve/execute Sprint 3)</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${selectFilter('status', 'Status', f.status, ['proposed','approved','rejected','executed','failed','cancelled','blocked'])}
      ${selectFilter('riskLevel', 'Risk', f.riskLevel, ['low','medium','high','critical'])}
      ${inputFilter('actionType', 'Action type', f.actionType)}
      ${selectFilter('approvalRequired', 'Approval req.', f.approvalRequired, ['true','false'])}
      ${inputFilter('tenantId', 'Tenant', f.tenantId)}
      <button id="cba-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  if (s.kind === 'error') return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  const { actions, summary } = s;
  return `${header}
    ${summaryStrip(summary)}
    <div class="rt-card">
      <div class="rt-card-title">Actions (${actions.length})</div>
      ${actions.length === 0
        ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen action yok</div></div>'
        : `<table style="width:100%;font-size:13px;">
            <thead><tr>
              <th style="text-align:left;">ID</th><th style="text-align:left;">Type</th>
              <th style="text-align:left;">Tenant</th><th style="text-align:left;">Risk</th>
              <th style="text-align:left;">Status</th><th style="text-align:left;">Approval</th>
              <th style="text-align:left;">Created</th>
            </tr></thead>
            <tbody>${actions.map((a) => `
              <tr>
                <td>${escHtml(a.id.slice(0,8))}</td>
                <td><code style="font-size:11px;">${escHtml(a.actionType)}</code></td>
                <td>${escHtml(a.tenantId)}</td>
                <td>${escHtml(a.riskLevel)}</td>
                <td>${escHtml(a.status)}</td>
                <td>${a.approvalRequired ? '<span style="color:var(--warn,#fb0);">required</span>' : '<span style="color:var(--muted);">no</span>'}</td>
                <td style="color:var(--muted);font-size:12px;">${escHtml(a.createdAt)}</td>
              </tr>
              <tr><td colspan="7" style="padding:0 0 4px 0;">
                ${policyGateChips(a.policyGate)}
              </td></tr>
              <tr><td colspan="7" style="padding:0 0 12px 0;">
                <details><summary style="cursor:pointer;font-size:11px;color:var(--muted);">proposedPayload</summary>
                  <pre style="background:var(--bg2);padding:8px;border-radius:4px;font-size:11px;white-space:pre-wrap;">${escHtml(JSON.stringify(a.proposedPayload, null, 2))}</pre>
                </details>
              </td></tr>`).join('')}
            </tbody>
          </table>`
      }
    </div>`;
}

function summaryStrip(s: ActionsSummary): string {
  return `<div class="rt-card" style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;">
    <span><strong>${s.total}</strong> total</span>
    ${chips('status', s.byStatus)}
    ${chips('risk', s.byRiskLevel)}
    <span style="color:var(--muted);">approval ${s.byApprovalRequired.true}/${s.byApprovalRequired.false}</span>
  </div>`;
}
// Sprint 3 PR-E4 — per-row policy gate chip strip. Read-only; tıklama yok.
function policyGateChips(gate: PolicyGateView | Record<string, never> | undefined): string {
  const baseStyle = 'font-size:11px;padding:2px 6px;border-radius:8px;margin-right:4px;background:var(--bg2);';
  const isLegacy = !gate || (typeof gate === 'object' && Object.keys(gate).length === 0);
  if (isLegacy) {
    return `<span style="color:var(--muted);font-size:11px;">policy: <span style="${baseStyle}">legacy</span></span>`;
  }
  const g = gate as PolicyGateView;
  const chips: string[] = [];
  if (typeof g.actionLevel === 'number') chips.push(`<span style="${baseStyle}">L${g.actionLevel}</span>`);
  if (g.humanRequired) chips.push(`<span style="${baseStyle}background:#5a3a3a;">human</span>`);
  if (g.councilRequired) chips.push(`<span style="${baseStyle}background:#3a2a5a;">council:${escHtml(String(g.councilRequired))}</span>`);
  if (g.executeAllowed === false) chips.push(`<span style="${baseStyle}background:#3a3a3a;">execute:blocked</span>`);
  if (g.outboundAllowed === false) chips.push(`<span style="${baseStyle}background:#3a3a3a;">outbound:blocked</span>`);
  if (g.llmAllowed === false) chips.push(`<span style="${baseStyle}background:#3a3a3a;">llm:blocked</span>`);
  return `<span style="color:var(--muted);font-size:11px;">policy: ${chips.join('')}</span>`;
}

function chips(label: string, m: Record<string, number>): string {
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${Object.entries(m).map(([k,v]) => `<span style="background:var(--bg2);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}
function selectFilter(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<select id="cba-f-${id}" class="form-select" style="width:130px;padding:4px 8px;"><option value="">(any)</option>${opts.map(o => `<option value="${escHtml(o)}"${o===value?' selected':''}>${escHtml(o)}</option>`).join('')}</select></label>`;
}
function inputFilter(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<input id="cba-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" /></label>`;
}
function attachHandlers(): void {
  const btn = document.getElementById('cba-apply');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const getV = (id: string) => (document.getElementById(`cba-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value || undefined;
    filters = {
      status: getV('status'),
      riskLevel: getV('riskLevel'),
      actionType: getV('actionType'),
      approvalRequired: getV('approvalRequired') as 'true' | 'false' | undefined,
      tenantId: getV('tenantId'),
      limit: 100,
    };
    await loadCbActions();
    renderCbActions();
  });
}
